import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { trackFilter, trackAction, trackTabChange, trackModalOpen, trackSearch, trackDownload } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Package,
  Plus,
  Search,
  Download,
  Upload,
  Trash2,
  Edit,
  MoreVertical,
  Loader2,
  MapPin,
  Hash,
  AlertTriangle,
  CheckCircle,
  Wrench,
  XCircle,
  RefreshCw,
  Handshake,
  History,
  RotateCcw,
  Archive,
  CheckSquare,
  Square,
  ScanLine,
  FileSpreadsheet,
  AlertCircle,
  Filter,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadFile } from '../lib/utils';
import { EquipmentModal, UnitModal, BulkUnitModal, ConditionHistoryModal } from '../components/inventory';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Eye } from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';

const InventoryPage = () => {
  const { api, isAdmin, user, hasPermission } = useAuth();
  const websocket = useWebSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  
  // Tab state for inventory vs deleted
  const [activeInventoryTab, setActiveInventoryTab] = useState('active');
  const [deletedEquipment, setDeletedEquipment] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  
  // Units state
  const [equipmentUnits, setEquipmentUnits] = useState({});
  const [loadingUnits, setLoadingUnits] = useState({});
  const [reconciling, setReconciling] = useState(false);
  const [statusCounts, setStatusCounts] = useState(null); // Backend-accurate counts
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
  const [isBulkUnitModalOpen, setIsBulkUnitModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);
  const [selectedEquipmentForUnits, setSelectedEquipmentForUnits] = useState(null);
  const [selectedEquipmentForHistory, setSelectedEquipmentForHistory] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Import report state
  const [importReport, setImportReport] = useState(null);
  
  // Check if user can bulk delete
  const canBulkDelete = hasPermission('bulk_delete_inventory');

  // v5.4.15 — Owner column: visible only for privileged roles
  const canSeeOwnerColumn = ['admin', 'superadmin', 'supervisor', 'auditor'].includes(user?.role);
  const [showOwnerColumn, setShowOwnerColumn] = useState(() => {
    if (!canSeeOwnerColumn) return false;
    const stored = localStorage.getItem('inv-show-owner-col');
    return stored === null ? true : stored === '1';
  });
  useEffect(() => {
    if (canSeeOwnerColumn) {
      localStorage.setItem('inv-show-owner-col', showOwnerColumn ? '1' : '0');
    }
  }, [showOwnerColumn, canSeeOwnerColumn]);

  // Scanner state
  const [showInventoryScanner, setShowInventoryScanner] = useState(false);

  // Unit detail modal state
  const [showUnitDetail, setShowUnitDetail] = useState(false);
  const [unitDetailData, setUnitDetailData] = useState(null);
  const [unitDetailLoading, setUnitDetailLoading] = useState(false);

  // v5.2.0 catalog: 10 canonical statuses with icons. See /lib/statusCatalog.js for labels/colors.
  const statusOptions = [
    { value: 'disponible',            label: 'Disponible',                icon: CheckCircle,    color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { value: 'asignado',              label: 'Asignado',                  icon: Package,        color: 'text-blue-500',    bg: 'bg-blue-500/10' },
    { value: 'en_prestamo',           label: 'En Préstamo',               icon: Handshake,      color: 'text-purple-500',  bg: 'bg-purple-500/10' },
    { value: 'mantenimiento_interno', label: 'Mantenimiento Interno',     icon: Wrench,         color: 'text-amber-500',   bg: 'bg-amber-500/10' },
    { value: 'mantenimiento_externo', label: 'Mantenimiento Externo',     icon: Wrench,         color: 'text-orange-500',  bg: 'bg-orange-500/10' },
    { value: 'falla',                 label: 'Con Falla',                 icon: AlertTriangle,  color: 'text-red-500',     bg: 'bg-red-500/10' },
    { value: 'perdido_recuperar',     label: 'Perdido a Recuperar',       icon: XCircle,        color: 'text-yellow-500',  bg: 'bg-yellow-500/10' },
    { value: 'perdido_definitivo',    label: 'Perdido Definitivo',        icon: XCircle,        color: 'text-rose-500',    bg: 'bg-rose-500/10' },
    { value: 'baja_perdida',          label: 'Dado de Baja por Pérdida',  icon: XCircle,        color: 'text-slate-500',   bg: 'bg-slate-500/10' },
    { value: 'baja_definitivo',       label: 'Dado de Baja Definitivo',   icon: XCircle,        color: 'text-zinc-500',    bg: 'bg-zinc-500/10' },
  ];

  // Map any legacy status value to its canonical replacement (display tolerance)
  const _legacyStatusMap = {
    mantenimiento: 'mantenimiento_interno',
    perdido: 'perdido_recuperar',
    baja: 'baja_definitivo',
  };
  const normalizeStatusValue = (s) => (s ? (_legacyStatusMap[s] || s) : s);

  const getStatusInfo = (status) => {
    const canonical = normalizeStatusValue(status);
    return statusOptions.find(s => s.value === canonical) || statusOptions[0];
  };

  // Handle URL status filter on mount and changes
  useEffect(() => {
    const urlStatus = searchParams.get('status');
    if (urlStatus && urlStatus !== statusFilter) {
      setStatusFilter(urlStatus);
      const statusInfo = statusOptions.find(s => s.value === urlStatus);
      if (statusInfo) {
        toast.info(`Mostrando equipos: ${statusInfo.label}`);
      }
    }
  }, [searchParams]);

  // Update URL when status filter changes
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    trackFilter('Estado', value);
    if (value === 'all') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', value);
    }
    setSearchParams(searchParams);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setLocationFilter('all');
    setStatusFilter('all');
    setSearchParams({});
    trackAction('Limpió filtros', 'inventory');
  };

  // Track filter changes
  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value);
    trackFilter('Categoría', value);
  };

  const handleLocationFilterChange = (value) => {
    setLocationFilter(value);
    trackFilter('Ubicación', value);
  };

  // Bulk selection functions
  const toggleSelectItem = (itemId, e) => {
    e.stopPropagation();
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === filteredEquipment.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredEquipment.map(eq => eq.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    const justification = window.prompt(
      `¿Estás seguro de eliminar ${selectedItems.size} equipo(s)?\n\nPor favor, ingresa una justificación (mínimo 10 caracteres):`,
      ''
    );
    
    if (!justification) return;
    if (justification.length < 10) {
      toast.error('La justificación debe tener al menos 10 caracteres');
      return;
    }
    
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedItems);
      const response = await api().post('/equipment/bulk-delete', { 
        ids: ids,
        justification: justification
      });
      
      let message = `${response.data.deleted_count || 0} equipo(s) eliminado(s)`;
      if (response.data.skipped?.length > 0) {
        message += `. ${response.data.skipped.length} omitido(s) por movimientos pendientes`;
        response.data.skipped.forEach(s => {
          toast.warning(`${s.name}: ${s.reason}`, { duration: 5000 });
        });
      }
      toast.success(message);
      setSelectedItems(new Set());
      loadData();
      loadDeletedEquipment();
    } catch (error) {
      console.error('Bulk delete error:', error);
      const errorMsg = error.response?.data?.detail || 'Error al eliminar equipos';
      toast.error(errorMsg);
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    loadData();
    loadAllUnitsForStats();
  }, []);

  // Reload data when WebSocket message received
  useEffect(() => {
    if (websocket?.lastMessage?.type?.includes('unit')) {
      loadData();
      loadAllUnitsForStats();
    }
  }, [websocket?.lastMessage]);

  const loadAllUnitsForStats = async () => {
    try {
      const response = await api().get('/equipment-units?per_page=10000');
      const units = response.data?.items || response.data || [];
      // Group units by equipment_id
      const unitsByEquipment = {};
      units.forEach(unit => {
        if (!unitsByEquipment[unit.equipment_id]) {
          unitsByEquipment[unit.equipment_id] = [];
        }
        unitsByEquipment[unit.equipment_id].push(unit);
      });
      setEquipmentUnits(unitsByEquipment);
    } catch (error) {
      console.error('Error loading all units:', error);
    }
  };

  const handleReconcile = async () => {
    if (!window.confirm('¿Ejecutar reconciliación de inventario?\n\nEsto sincronizará el estado de las unidades con los movimientos existentes.\n\nEquipos en modo "Automático" asignarán unidades aleatorias.\nEquipos en modo "Manual" sin seriales especificados mostrarán errores.')) {
      return;
    }
    
    setReconciling(true);
    try {
      const response = await api().post('/inventory/reconcile');
      const { units_marked_asignado, units_marked_disponible, errors, details } = response.data;
      
      // Show detailed result
      const totalChanges = (units_marked_asignado || 0) + (units_marked_disponible || 0);
      
      if (totalChanges === 0 && (!errors || errors.length === 0)) {
        toast.info('No hay cambios necesarios. El inventario ya está sincronizado.', {
          duration: 5000
        });
      } else if (errors && errors.length > 0) {
        toast.warning(`Reconciliación completada con ${errors.length} advertencias`, {
          description: `${units_marked_asignado || 0} asignados, ${units_marked_disponible || 0} disponibles.\n${errors.slice(0, 2).join('\n')}`,
          duration: 10000
        });
      } else {
        toast.success(`Reconciliación exitosa: ${units_marked_asignado || 0} asignados, ${units_marked_disponible || 0} disponibles`, {
          duration: 5000
        });
      }
      
      // Reload data
      loadData();
      loadAllUnitsForStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reconciliar');
    } finally {
      setReconciling(false);
    }
  };

  const handleCleanupDuplicates = async () => {
    if (!window.confirm('¿Eliminar unidades duplicadas?\n\nEsto buscará unidades con el mismo serial y equipo, y eliminará los duplicados manteniendo solo una copia.')) {
      return;
    }
    
    setReconciling(true);
    try {
      const response = await api().post('/inventory/cleanup-duplicate-units');
      const { cleaned, duplicate_serials } = response.data;
      
      if (cleaned === 0) {
        toast.info('No se encontraron unidades duplicadas.');
      } else {
        toast.success(`Se eliminaron ${cleaned} unidades duplicadas`);
      }
      
      loadData();
      loadAllUnitsForStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al limpiar duplicados');
    } finally {
      setReconciling(false);
    }
  };

  const handleCleanupOrphans = async () => {
    if (!window.confirm('¿Eliminar unidades huérfanas?\n\nEsto eliminará unidades que pertenecen a tipos de equipo que ya fueron eliminados.')) {
      return;
    }
    
    setReconciling(true);
    try {
      const response = await api().post('/inventory/cleanup-deleted-equipment-units');
      const { cleaned } = response.data;
      
      if (cleaned === 0) {
        toast.info('No se encontraron unidades huérfanas.');
      } else {
        toast.success(`Se eliminaron ${cleaned} unidades huérfanas`);
      }
      
      loadData();
      loadAllUnitsForStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al limpiar huérfanas');
    } finally {
      setReconciling(false);
    }
  };

  const handleFullCleanup = async () => {
    if (!window.confirm('¿Ejecutar limpieza completa?\n\nEsto hará:\n1. Eliminar unidades duplicadas\n2. Eliminar unidades huérfanas\n3. Reconciliar estados\n\n¿Continuar?')) {
      return;
    }
    
    setReconciling(true);
    try {
      // Step 1: Cleanup duplicates
      const dupRes = await api().post('/inventory/cleanup-duplicate-units');
      
      // Step 2: Cleanup orphans
      const orphRes = await api().post('/inventory/cleanup-deleted-equipment-units');
      
      // Step 3: Reconcile
      const recRes = await api().post('/inventory/reconcile');
      
      const totalCleaned = (dupRes.data.cleaned || 0) + (orphRes.data.cleaned || 0);
      const totalReconciled = (recRes.data.units_marked_asignado || 0) + (recRes.data.units_marked_disponible || 0);
      
      toast.success(`Limpieza completa: ${totalCleaned} eliminadas, ${totalReconciled} reconciliadas`);
      
      loadData();
      loadAllUnitsForStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en limpieza');
    } finally {
      setReconciling(false);
    }
  };

  const handleRunStatusMigration = async () => {
    if (!window.confirm('¿Ejecutar la migración de estados v5.2.0?\n\nEsto convertirá:\n• "mantenimiento" → "mantenimiento_interno"\n• "perdido" → "perdido_recuperar"\n• "baja" → "baja_definitivo"\n\nEs idempotente: no hace nada si ya se aplicó.')) return;
    setReconciling(true);
    try {
      const res = await api().post('/inventory/run-status-migration-v520');
      const total = res.data.total_changes || 0;
      if (total === 0) {
        toast.info('No se encontraron registros legacy. La migración ya estaba aplicada.');
      } else {
        toast.success(`Migración v5.2.0 aplicada: ${total} registro(s) actualizado(s)`);
      }
      loadData();
      loadAllUnitsForStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error ejecutando la migración');
    } finally {
      setReconciling(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const apiInstance = api();
      const [equipmentRes, categoriesRes, locationsRes, statusCountsRes] = await Promise.all([
        apiInstance.get('/equipment'),
        apiInstance.get('/categories'),
        apiInstance.get('/locations'),
        apiInstance.get('/inventory/status-counts').catch(() => ({ data: null }))
      ]);
      setEquipment(equipmentRes.data);
      setCategories(categoriesRes.data);
      setLocations(locationsRes.data);
      if (statusCountsRes.data) {
        setStatusCounts(statusCountsRes.data);
      }
    } catch (error) {
      console.error('Error al cargar datos de inventario:', error);
      // Log to debugger
      try {
        await api().post('/debug/log', {
          level: 'error',
          source: 'InventoryPage.fetchData',
          message: `Error al cargar datos: ${error.message || 'Unknown error'}`,
          details: {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            url: error.config?.url
          }
        });
      } catch (logError) {
        console.error('Failed to log error:', logError);
      }
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  // Load deleted equipment for restore functionality
  const loadDeletedEquipment = async () => {
    setLoadingDeleted(true);
    try {
      const response = await api().get('/equipment?include_deleted=true');
      // Filter to only show deleted items
      const deleted = response.data.filter(eq => eq.is_deleted === true);
      setDeletedEquipment(deleted);
    } catch (error) {
      console.error('Error loading deleted equipment:', error);
      toast.error('Error al cargar equipos eliminados');
    } finally {
      setLoadingDeleted(false);
    }
  };
  
  // Restore deleted equipment
  const handleRestoreEquipment = async (equipmentId, equipmentName) => {
    if (!window.confirm(`¿Restaurar el equipo "${equipmentName}"?`)) return;
    
    try {
      await api().post(`/equipment/${equipmentId}/restore`);
      toast.success(`Equipo "${equipmentName}" restaurado`);
      loadDeletedEquipment();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar equipo');
    }
  };
  
  // Load deleted equipment when tab changes
  useEffect(() => {
    if (activeInventoryTab === 'deleted') {
      loadDeletedEquipment();
    }
  }, [activeInventoryTab]);

  const loadEquipmentUnits = async (equipmentId, force = false) => {
    if (!force && equipmentUnits[equipmentId]) return; // Already loaded
    
    setLoadingUnits(prev => ({ ...prev, [equipmentId]: true }));
    try {
      const response = await api().get(`/equipment/${equipmentId}/units`);
      setEquipmentUnits(prev => ({ ...prev, [equipmentId]: response.data }));
    } catch (error) {
      console.error('Error loading units:', error);
    } finally {
      setLoadingUnits(prev => ({ ...prev, [equipmentId]: false }));
    }
  };

  // Re-fetch units for all currently expanded equipments (used after imports)
  const refreshLoadedUnits = async () => {
    const expandedIds = Object.keys(equipmentUnits);
    await Promise.all(expandedIds.map(id => loadEquipmentUnits(id, true)));
  };

  // === Category filter — duplicate-safe normalization ===
  // Some installations end up with multiple categories that share the same
  // human-readable name ("Cómputo", " Cómputo", "computo"...). Each duplicate
  // has its own UUID, so equipment ends up split between them. The previous
  // strict `eq.category_id === categoryFilter` check missed all the equipment
  // assigned to the duplicate categories.
  //
  // Fix: when the user picks a category in the dropdown, we expand the filter
  // to ALL category_ids whose normalized name matches the picked category's
  // normalized name (Unicode NFD strip + lowercase + trim + collapse spaces).
  const normalizeCategoryName = (s) =>
    (s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')   // strip accents
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');

  const categoryNameGroups = useMemo(() => {
    const m = {};
    (categories || []).forEach((c) => {
      const k = normalizeCategoryName(c.name);
      if (!k) return;
      if (!m[k]) m[k] = [];
      m[k].push(c.id);
    });
    return m;
  }, [categories]);

  const selectedCategoryName = useMemo(() => {
    if (categoryFilter === 'all') return null;
    const c = (categories || []).find((c) => c.id === categoryFilter);
    return c ? c.name : null;
  }, [categoryFilter, categories]);

  const matchingCategoryIds = useMemo(() => {
    if (categoryFilter === 'all' || !selectedCategoryName) {
      return new Set();
    }
    const key = normalizeCategoryName(selectedCategoryName);
    return new Set(categoryNameGroups[key] || [categoryFilter]);
  }, [categoryFilter, selectedCategoryName, categoryNameGroups]);

  const filteredEquipment = equipment.filter(eq => {
    const term = searchTerm.toLowerCase();
    const matchSearch = !term || 
      eq.name.toLowerCase().includes(term) ||
      eq.brand?.toLowerCase().includes(term) ||
      eq.model?.toLowerCase().includes(term) ||
      (equipmentUnits[eq.id] || []).some(u => 
        u?.serial_number?.toLowerCase().includes(term)
      );
    const matchCategory = categoryFilter === 'all' || matchingCategoryIds.has(eq.category_id) || (
      // Defensive fallback for orphan equipment whose category_id no longer
      // exists but whose category_name still matches by normalized comparison.
      !!selectedCategoryName && normalizeCategoryName(eq.category_name) === normalizeCategoryName(selectedCategoryName)
    );
    const matchLocation = locationFilter === 'all' || 
      eq.locations?.some(loc => loc.location_id === locationFilter);
    
    // Filter by status - check if equipment has any units with the selected status.
    // Legacy aggregate values (mantenimiento, perdido, baja) match all their sub-states.
    let matchStatus = statusFilter === 'all';
    const _statusMatchesFilter = (unitStatus) => {
      if (!unitStatus) return false;
      if (statusFilter === 'mantenimiento')
        return ['mantenimiento_interno', 'mantenimiento_externo', 'mantenimiento', 'en_mantenimiento'].includes(unitStatus);
      if (statusFilter === 'perdido')
        return ['perdido_recuperar', 'perdido_definitivo', 'perdido', 'faltante'].includes(unitStatus);
      if (statusFilter === 'baja' || statusFilter === 'dado_de_baja')
        return ['baja_perdida', 'baja_definitivo', 'baja', 'dado_de_baja'].includes(unitStatus);
      return unitStatus === statusFilter;
    };
    if (!matchStatus) {
      const eqUnits = (equipmentUnits[eq.id] || []).filter(Boolean);
      if (eqUnits.length > 0) {
        matchStatus = eqUnits.some(unit => unit && _statusMatchesFilter(unit.status));
      } else {
        // If no units loaded, check based on locations data
        if (statusFilter === 'disponible') {
          matchStatus = eq.locations?.some(loc => (loc.available || 0) > 0);
        } else if (statusFilter === 'asignado') {
          matchStatus = eq.locations?.some(loc => (loc.assigned || 0) > 0);
        } else {
          // For en_prestamo / mantenimiento_* / falla / perdido_* / baja_*: show equipment
          // and let the user expand to see individual units (location data does not track these)
          matchStatus = (eq.total_stock || 0) > 0;
        }
      }
    }
    
    return matchSearch && matchCategory && matchLocation && matchStatus;
  });

  // Calculate totals by status - prefer backend counts when available
  const calculateStatusTotals = () => {
    // If we have backend counts, use those (most accurate).
    // Backend may return either canonical or legacy keys; we accept both for safety.
    if (statusCounts) {
      const sc = statusCounts;
      return {
        'todos': sc.todos || 0,
        'disponible': sc.disponible || 0,
        'asignado': sc.asignado || 0,
        'en_prestamo': sc.en_prestamo || 0,
        'mantenimiento_interno': sc.mantenimiento_interno || sc.mantenimiento || 0,
        'mantenimiento_externo': sc.mantenimiento_externo || 0,
        'falla': sc.falla || 0,
        'perdido_recuperar': sc.perdido_recuperar || sc.perdido || 0,
        'perdido_definitivo': sc.perdido_definitivo || 0,
        'baja_perdida': sc.baja_perdida || 0,
        'baja_definitivo': sc.baja_definitivo || sc.dado_de_baja || sc.baja || 0,
      };
    }
    
    // Fallback: Calculate from loaded units
    const totals = {};
    statusOptions.forEach(s => totals[s.value] = 0);
    
    // Count from units (primary source). Legacy values are remapped to canonical.
    const unitsArray = Object.values(equipmentUnits).flat().filter(Boolean);
    
    if (unitsArray.length > 0) {
      unitsArray.forEach(unit => {
        if (unit && unit.status) {
          const status = normalizeStatusValue(unit.status);
          totals[status] = (totals[status] || 0) + 1;
        } else if (unit) {
          totals['disponible'] = (totals['disponible'] || 0) + 1;
        }
      });
    } else {
      // Last resort: Use equipment summary from backend
      equipment.forEach(eq => {
        totals['disponible'] = (totals['disponible'] || 0) + (eq.total_available || 0);
        totals['asignado'] = (totals['asignado'] || 0) + (eq.total_assigned || 0);
      });
    }
    
    return totals;
  };

  const statusTotals = calculateStatusTotals();

  const openCreateModal = () => {
    setEditingEquipment(null);
    setIsModalOpen(true);
  };

  const openEditModal = (eq) => {
    setEditingEquipment(eq);
    setIsModalOpen(true);
  };

  const openUnitModal = (eq, unit = null) => {
    setSelectedEquipmentForUnits(eq);
    setEditingUnit(unit);
    setIsUnitModalOpen(true);
  };

  const openBulkUnitModal = (eq) => {
    setSelectedEquipmentForUnits(eq);
    setIsBulkUnitModalOpen(true);
  };

  const handleEquipmentSuccess = () => {
    loadData();
  };

  const handleUnitSuccess = async () => {
    if (selectedEquipmentForUnits) {
      // Reload units for this specific equipment without clearing first
      try {
        const response = await api().get(`/equipment/${selectedEquipmentForUnits.id}/units`);
        setEquipmentUnits(prev => ({ ...prev, [selectedEquipmentForUnits.id]: response.data }));
      } catch (error) {
        console.error('Error reloading units:', error);
      }
      // Also reload all units for accurate stats
      loadAllUnitsForStats();
    }
  };

  const handleDeleteUnit = async (unitId, equipmentId) => {
    if (!window.confirm('¿Eliminar esta unidad?')) return;
    try {
      await api().delete(`/equipment-units/${unitId}`);
      toast.success('Unidad eliminada');
      setEquipmentUnits(prev => ({
        ...prev,
        [equipmentId]: prev[equipmentId]?.filter(u => u.id !== unitId)
      }));
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openUnitDetail = async (unitId) => {
    setUnitDetailLoading(true);
    setShowUnitDetail(true);
    try {
      const res = await api().get(`/equipment-units/${unitId}/details`);
      setUnitDetailData(res.data);
    } catch {
      toast.error('Error cargando detalles');
      setShowUnitDetail(false);
    } finally {
      setUnitDetailLoading(false);
    }
  };

  const [resolvingFault, setResolvingFault] = useState(false);
  const handleResolveFault = async () => {
    if (!unitDetailData?.unit?.id) return;
    setResolvingFault(true);
    try {
      await api().put(`/equipment-units/${unitDetailData.unit.id}`, {
        status: 'disponible',
        fault_reason: null,
        resolve_maintenance: true
      });
      toast.success('Falla resuelta. Unidad disponible y mantenimiento completado.');
      setShowUnitDetail(false);
      loadAllUnitsForStats();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al resolver falla');
    } finally {
      setResolvingFault(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este equipo y todas sus unidades?')) return;
    try {
      await api().delete(`/equipment/${id}`);
      toast.success('Equipo eliminado');
      trackAction('Eliminó equipo', 'equipment', id);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleExport = async () => {
    try {
      // v5.3.5: pass active inventory filters to the backend so the exported
      // Excel mirrors exactly what the user is seeing on screen.
      const params = {};
      if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
      if (categoryFilter && categoryFilter !== 'all') params.category_id = categoryFilter;
      if (locationFilter && locationFilter !== 'all') params.location_id = locationFilter;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;

      const hasFilters = Object.keys(params).length > 0;
      const response = await api().get('/equipment/export/excel', {
        responseType: 'blob',
        params,
      });
      const filename = `Inventario_${new Date().toISOString().split('T')[0]}.xlsx`;
      downloadFile(response.data, filename);
      toast.success(
        hasFilters
          ? `Inventario exportado con filtros activos (${Object.keys(params).length})`
          : 'Inventario completo exportado'
      );
      trackDownload(filename, 'Excel');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleImportUnits = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api().post('/equipment/import/units', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = response.data;
      // Show import report modal
      setImportReport({ ...data, filename: file.name });
      trackAction('Importó unidades', 'equipment', file.name);
      await loadData();
      // Re-fetch units currently expanded so updates reflect immediately
      await refreshLoadedUnits();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error al importar unidades';
      setImportReport({
        imported: 0, updated: 0, skipped: 0, total_errors: 1,
        errors: [detail], details: [], columns_found: [], total_rows: 0,
        message: 'Error en la importación', filename: file.name
      });
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api().post('/equipment/import/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = response.data;
      setImportReport({ ...data, filename: file.name });
      trackAction('Importó equipos Excel', 'equipment', file.name);
      await loadData();
      // Re-fetch units currently expanded so updates reflect immediately
      await refreshLoadedUnits();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error al importar';
      setImportReport({
        imported: 0, updated: 0, skipped: 0, total_errors: 1,
        errors: [detail], details: [], columns_found: [], total_rows: 0,
        message: 'Error en la importación', filename: file.name
      });
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  // Group units by location for display
  const groupUnitsByLocation = (units) => {
    const grouped = {};
    // Filter out null/undefined units
    (units || []).filter(Boolean).forEach(unit => {
      if (!unit) return;
      const locId = unit.location_id || 'unknown';
      if (!grouped[locId]) {
        grouped[locId] = {
          location_id: locId,
          location_name: unit.location_name || locations.find(l => l.id === locId)?.name || 'Sin ubicación',
          units: []
        };
      }
      grouped[locId].units.push(unit);
    });
    return Object.values(grouped);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inventario</h1>
          <p className="text-muted-foreground mt-1">Gestiona equipos y unidades individuales</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(isAdmin() || hasPermission('import_inventory')) && (
            <>
              <input type="file" id="import-units-file" accept=".xlsx,.xls" onChange={handleImportUnits} className="hidden" />
              <Button variant="outline" onClick={() => document.getElementById('import-units-file').click()} disabled={importing}>
                {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Importar
              </Button>
            </>
          )}
          <Button onClick={handleExport} variant="outline" data-testid="inventory-export-btn"
            title={(searchTerm || categoryFilter !== 'all' || locationFilter !== 'all' || statusFilter !== 'all')
              ? 'Exportar respetando los filtros activos'
              : 'Exportar todo el inventario'}>
            <Download className="h-4 w-4 mr-2" />
            Exportar
            {(searchTerm || categoryFilter !== 'all' || locationFilter !== 'all' || statusFilter !== 'all') && (
              <span className="ml-2 text-[10px] font-semibold rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 px-1.5 py-0.5"
                    data-testid="export-filtered-badge">
                filtrado
              </span>
            )}
          </Button>
          {hasPermission('reconcile_inventory') && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  disabled={reconciling}
                  title="Herramientas de mantenimiento de inventario"
                >
                  {reconciling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Reconciliar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleReconcile}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sincronizar estados
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleCleanupDuplicates}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar duplicados
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleCleanupOrphans}>
                  <Archive className="h-4 w-4 mr-2" />
                  Eliminar huérfanas
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleFullCleanup} className="text-amber-500">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Limpieza completa
                </DropdownMenuItem>
                {user?.role === 'superadmin' && (
                  <DropdownMenuItem onClick={handleRunStatusMigration} className="text-purple-500" data-testid="run-status-migration-btn">
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Migrar estados (v5.2.0)
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button onClick={openCreateModal} className="btn-primary" data-testid="add-equipment-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Equipo
          </Button>
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {/* "Todos" card - always first */}
        <Card 
          className={`cursor-pointer transition-all hover:scale-105 ${statusFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => handleStatusFilterChange('all')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Package className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statusTotals['todos'] || 0}</p>
                <p className="text-xs text-muted-foreground">Todos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {statusOptions.map((status) => {
          const StatusIcon = status.icon;
          return (
            <Card 
              key={status.value} 
              className={`cursor-pointer transition-all hover:scale-105 ${statusFilter === status.value ? 'ring-2 ring-primary' : ''}`}
              onClick={() => handleStatusFilterChange(statusFilter === status.value ? 'all' : status.value)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${status.bg}`}>
                    <StatusIcon className={`h-4 w-4 ${status.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{statusTotals[status.value] || 0}</p>
                    <p className="text-xs text-muted-foreground">{status.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Inventory Tabs - Active vs Deleted */}
      {hasPermission('view_deleted_inventory') && (
        <div className="inline-flex h-10 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground gap-1">
          <button
            type="button"
            onClick={() => { setActiveInventoryTab('active'); trackTabChange('Inventario Activo'); }}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeInventoryTab === 'active' 
                ? 'bg-background text-foreground shadow-sm' 
                : 'hover:bg-background/50'
            }`}
          >
            <Package className="h-4 w-4 mr-2" />
            Inventario Activo ({equipment.length})
          </button>
          <button
            type="button"
            onClick={() => { setActiveInventoryTab('deleted'); trackTabChange('Papelera'); }}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeInventoryTab === 'deleted' 
                ? 'bg-background text-foreground shadow-sm' 
                : 'hover:bg-background/50'
            }`}
          >
            <Archive className="h-4 w-4 mr-2" />
            Papelera ({deletedEquipment.length})
          </button>
        </div>
      )}

      {/* Deleted Equipment Tab Content */}
      {activeInventoryTab === 'deleted' && hasPermission('view_deleted_inventory') && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Archive className="h-5 w-5" />
                Equipos Eliminados
              </CardTitle>
              <CardDescription>
                Equipos que han sido eliminados y pueden ser restaurados
              </CardDescription>
            </div>
            {deletedEquipment.length > 0 && hasPermission('bulk_delete_inventory') && (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={async () => {
                  if (!window.confirm(`¿VACIAR PAPELERA?\n\nEsto eliminará PERMANENTEMENTE ${deletedEquipment.length} equipo(s) y todas sus unidades.\n\nEsta acción NO se puede deshacer.`)) return;
                  try {
                    const response = await api().delete('/equipment/permanent-all');
                    toast.success(`Papelera vaciada: ${response.data.equipment_deleted} equipos eliminados`);
                    loadDeletedEquipment();
                    loadData();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Error al vaciar papelera');
                  }
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Vaciar Papelera
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {loadingDeleted ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : deletedEquipment.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Archive className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No hay equipos eliminados</p>
              </div>
            ) : (
              <div className="space-y-2">
                {deletedEquipment.map(eq => (
                  <div key={eq.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <Package className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{eq.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Eliminado: {eq.deleted_at ? new Date(eq.deleted_at).toLocaleDateString() : 'Fecha desconocida'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRestoreEquipment(eq.id, eq.name)}
                        className="text-green-500 hover:text-green-400"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restaurar
                      </Button>
                      {hasPermission('delete_inventory') && (
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={async () => {
                            if (!window.confirm(`¿Eliminar "${eq.name}" PERMANENTEMENTE?\n\nEsta acción NO se puede deshacer.`)) return;
                            try {
                              await api().delete(`/equipment/${eq.id}/permanent`);
                              toast.success(`"${eq.name}" eliminado permanentemente`);
                              loadDeletedEquipment();
                              loadData();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Error al eliminar');
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Active Equipment - Only show when active tab */}
      {activeInventoryTab === 'active' && (
        <>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar equipo, marca, modelo o serial..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="inventory-search-input"
            />
          </div>
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setShowInventoryScanner(true)}
            title="Escanear código de barras"
            data-testid="inventory-scanner-btn"
          >
            <ScanLine className="h-4 w-4" />
          </Button>
        </div>
        <Select value={categoryFilter} onValueChange={handleCategoryFilterChange}>
          <SelectTrigger><SelectValue placeholder="Categoría" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={locationFilter} onValueChange={handleLocationFilterChange}>
          <SelectTrigger><SelectValue placeholder="Ubicación" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las ubicaciones</SelectItem>
            {locations.map(loc => (
              <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
          <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {statusOptions.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
            {/* Legacy aggregate values — only visible when active (e.g. coming from Dashboard) */}
            {statusFilter === 'mantenimiento' && (
              <SelectItem value="mantenimiento">Mantenimiento (todas)</SelectItem>
            )}
            {statusFilter === 'perdido' && (
              <SelectItem value="perdido">Perdido (todas)</SelectItem>
            )}
            {(statusFilter === 'baja' || statusFilter === 'dado_de_baja') && (
              <SelectItem value={statusFilter}>Dado de Baja (todas)</SelectItem>
            )}
          </SelectContent>
        </Select>
        
        {/* Clear filters button */}
        {(statusFilter !== 'all' || categoryFilter !== 'all' || locationFilter !== 'all' || searchTerm) && (
          <Button variant="ghost" onClick={clearFilters} className="text-muted-foreground">
            <XCircle className="h-4 w-4 mr-2" />
            Limpiar filtros
          </Button>
        )}
      </div>

      {/* Active category filter indicator — shows real total of equipment families
          matching the chosen category (after duplicate-name normalization). */}
      {categoryFilter !== 'all' && (
        <div
          className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/20 text-sm"
          data-testid="category-filter-indicator"
        >
          <Filter className="h-4 w-4 text-primary" />
          <span>
            Mostrando <strong data-testid="category-filter-count">{filteredEquipment.length}</strong>{' '}
            {filteredEquipment.length === 1 ? 'familia' : 'familias'} en categoría:{' '}
            <strong>{selectedCategoryName}</strong>
            {matchingCategoryIds.size > 1 && (
              <span className="ml-2 text-xs text-muted-foreground">
                (incluye {matchingCategoryIds.size} categorías duplicadas con el mismo nombre)
              </span>
            )}
          </span>
        </div>
      )}

      {/* Aggregated unit summary across the currently filtered equipment.
          Updates in real-time as the user types in search or changes filters.
          When a specific status filter is active, the summary reflects ONLY the units
          matching that status across the filtered families. */}
      {(searchTerm || categoryFilter !== 'all' || locationFilter !== 'all' || statusFilter !== 'all') && filteredEquipment.length > 0 && (() => {
        const _statusFilterMatches = (us) => {
          if (!us) return false;
          if (statusFilter === 'mantenimiento')
            return ['mantenimiento_interno', 'mantenimiento_externo', 'mantenimiento', 'en_mantenimiento'].includes(us);
          if (statusFilter === 'perdido')
            return ['perdido_recuperar', 'perdido_definitivo', 'perdido', 'faltante'].includes(us);
          if (statusFilter === 'baja' || statusFilter === 'dado_de_baja')
            return ['baja_perdida', 'baja_definitivo', 'baja', 'dado_de_baja'].includes(us);
          return us === statusFilter;
        };
        const isStatusFiltered = statusFilter !== 'all';
        const totals = filteredEquipment.reduce(
          (acc, eq) => {
            if (isStatusFiltered) {
              // Count only units matching the active status filter
              const eqUnits = (equipmentUnits[eq.id] || []).filter(Boolean);
              acc.matching += eqUnits.filter(u => _statusFilterMatches(u.status)).length;
            } else {
              acc.total += Number(eq.total_stock || 0);
              acc.available += Number(eq.total_available || 0);
              acc.assigned += Number(eq.total_assigned || 0);
            }
            return acc;
          },
          { total: 0, available: 0, assigned: 0, matching: 0 }
        );
        const showLabel = filteredEquipment.length === 1
          ? `Mostrando: "${filteredEquipment[0].name}"`
          : `Mostrando: ${filteredEquipment.length} familias filtradas`;
        const statusLbl = isStatusFiltered
          ? (statusOptions.find(s => s.value === statusFilter)?.label
             || (statusFilter === 'mantenimiento' ? 'Mantenimiento'
                : statusFilter === 'perdido' ? 'Perdido'
                : statusFilter === 'baja' || statusFilter === 'dado_de_baja' ? 'Dado de Baja'
                : statusFilter))
          : null;
        return (
          <div
            className={`grid gap-3 ${isStatusFiltered ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}
            data-testid="inventory-summary-badges"
          >
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4 space-y-1">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Filtro activo</div>
                <div className="font-medium text-sm truncate" title={showLabel}>{showLabel}</div>
              </CardContent>
            </Card>
            {isStatusFiltered ? (
              <Card data-testid="summary-status-filtered">
                <CardContent className="p-4 space-y-1">
                  <div className="text-xs text-primary uppercase tracking-wide">Unidades {statusLbl}</div>
                  <div className="text-2xl font-bold text-primary">{totals.matching}</div>
                </CardContent>
              </Card>
            ) : (
              <>
                <Card data-testid="summary-total">
                  <CardContent className="p-4 space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">Unidades totales</div>
                    <div className="text-2xl font-bold">{totals.total}</div>
                  </CardContent>
                </Card>
                <Card data-testid="summary-available">
                  <CardContent className="p-4 space-y-1">
                    <div className="text-xs text-emerald-500 uppercase tracking-wide">Disponibles</div>
                    <div className="text-2xl font-bold text-emerald-400">{totals.available}</div>
                  </CardContent>
                </Card>
                <Card data-testid="summary-assigned">
                  <CardContent className="p-4 space-y-1">
                    <div className="text-xs text-amber-500 uppercase tracking-wide">Asignadas</div>
                    <div className="text-2xl font-bold text-amber-400">{totals.assigned}</div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        );
      })()}

      {/* Active filter indicator from Dashboard */}
      {searchParams.get('status') && (
        <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg border border-primary/20">
          <span className="text-sm">
            Filtrando desde Dashboard: <strong>{
              statusOptions.find(s => s.value === statusFilter)?.label
              || (statusFilter === 'mantenimiento' ? 'Mantenimiento (Interno + Externo)'
                : statusFilter === 'perdido' ? 'Perdido (Recuperar + Definitivo)'
                : statusFilter === 'baja' || statusFilter === 'dado_de_baja' ? 'Dado de Baja (Pérdida + Definitivo)'
                : statusFilter)
            }</strong>
          </span>
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Ver todos
          </Button>
        </div>
      )}

      {/* Bulk Selection Bar */}
      {canBulkDelete && activeInventoryTab === 'active' && filteredEquipment.length > 0 && (
        <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg border">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
          >
            {selectedItems.size === filteredEquipment.length && filteredEquipment.length > 0 ? (
              <CheckSquare className="h-5 w-5 text-primary" />
            ) : (
              <Square className="h-5 w-5" />
            )}
            {selectedItems.size === filteredEquipment.length && filteredEquipment.length > 0 
              ? 'Deseleccionar todos' 
              : 'Seleccionar todos'}
          </button>
          
          {selectedItems.size > 0 && (
            <>
              <span className="text-sm text-muted-foreground">
                {selectedItems.size} equipo(s) seleccionado(s)
              </span>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
              >
                {bulkDeleting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Eliminar seleccionados
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedItems(new Set())}
              >
                Cancelar
              </Button>
            </>
          )}
        </div>
      )}

      {/* Equipment List */}
      <Card>
        {/* v5.4.15 — Owner column toggle (only for admins/superadmins/supervisors/auditors) */}
        {canSeeOwnerColumn && (
          <div className="flex items-center justify-end gap-2 px-4 py-2 border-b text-xs text-muted-foreground">
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="toggle-owner-column">
              <input
                type="checkbox"
                checked={showOwnerColumn}
                onChange={(e) => setShowOwnerColumn(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              Mostrar columna Owner
            </label>
          </div>
        )}
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-4 space-y-2">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : filteredEquipment.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No hay equipos</p>
            </div>
          ) : (
            <Accordion type="multiple" className="w-full">
              {filteredEquipment.map((eq) => (
                <AccordionItem key={eq.id} value={eq.id} className="border-b">
                  <AccordionTrigger 
                    className="px-4 hover:no-underline"
                    onClick={() => loadEquipmentUnits(eq.id)}
                  >
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-4">
                        {/* Checkbox for bulk selection */}
                        {canBulkDelete && (
                          <button
                            onClick={(e) => toggleSelectItem(eq.id, e)}
                            className="p-1 hover:bg-muted rounded transition-colors"
                          >
                            {selectedItems.has(eq.id) ? (
                              <CheckSquare className="h-5 w-5 text-primary" />
                            ) : (
                              <Square className="h-5 w-5 text-muted-foreground" />
                            )}
                          </button>
                        )}
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Package className="h-5 w-5 text-primary" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">{eq.name}</p>
                          <p className="text-sm text-muted-foreground">{eq.category_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mr-4">
                        {/* v5.4.15 — Owner badge (admin-visible only) */}
                        {canSeeOwnerColumn && showOwnerColumn && (
                          <div className="text-center min-w-[110px]" data-testid={`owner-cell-${eq.id}`}>
                            {eq.created_by_name ? (
                              <>
                                <p className="text-xs font-medium truncate max-w-[140px]" title={eq.created_by_name}>{eq.created_by_name}</p>
                                <p className="text-[10px] text-muted-foreground">Owner</p>
                              </>
                            ) : (
                              <>
                                <p className="text-xs text-rose-500 font-medium">Sin owner</p>
                                <p className="text-[10px] text-muted-foreground">Owner</p>
                              </>
                            )}
                          </div>
                        )}
                        <div className="text-center">
                          <p className="text-lg font-bold text-primary">{eq.total_stock || 0}</p>
                          <p className="text-xs text-muted-foreground">Total</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-emerald-500">{eq.total_available || 0}</p>
                          <p className="text-xs text-muted-foreground">Disponible</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditModal(eq); }}>
                              <Edit className="h-4 w-4 mr-2" /> Editar Equipo
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedEquipmentForHistory(eq); setIsHistoryModalOpen(true); }}>
                              <History className="h-4 w-4 mr-2" /> Ver Historial
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openUnitModal(eq); }}>
                              <Plus className="h-4 w-4 mr-2" /> Agregar Unidad
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openBulkUnitModal(eq); }}>
                              <Hash className="h-4 w-4 mr-2" /> Agregar Múltiples
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(eq.id); }} className="text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" /> Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    {loadingUnits[eq.id] ? (
                      <div className="space-y-2">
                        <Skeleton className="h-20 w-full" />
                        <Skeleton className="h-20 w-full" />
                      </div>
                    ) : equipmentUnits[eq.id]?.length > 0 ? (
                      <div className="space-y-4">
                        {groupUnitsByLocation(equipmentUnits[eq.id]).map((locGroup) => (
                          <div key={locGroup.location_id} className="bg-muted/30 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                              <MapPin className="h-4 w-4 text-primary" />
                              <span className="font-medium">{locGroup.location_name}</span>
                              <Badge variant="outline">{locGroup.units.length} unidades</Badge>
                            </div>
                            <div className="space-y-2">
                              {locGroup.units
                                .filter(unit => {
                                  if (statusFilter === 'all') return true;
                                  const us = unit.status;
                                  // Legacy aggregate filters match all sub-states
                                  if (statusFilter === 'mantenimiento')
                                    return ['mantenimiento_interno', 'mantenimiento_externo', 'mantenimiento', 'en_mantenimiento'].includes(us);
                                  if (statusFilter === 'perdido')
                                    return ['perdido_recuperar', 'perdido_definitivo', 'perdido', 'faltante'].includes(us);
                                  if (statusFilter === 'baja' || statusFilter === 'dado_de_baja')
                                    return ['baja_perdida', 'baja_definitivo', 'baja', 'dado_de_baja'].includes(us);
                                  return us === statusFilter;
                                })
                                .filter(unit => {
                                  if (!searchTerm) return true;
                                  const term = searchTerm.toLowerCase();
                                  return unit.serial_number?.toLowerCase().includes(term) ||
                                    eq.name?.toLowerCase().includes(term) ||
                                    eq.brand?.toLowerCase().includes(term) ||
                                    eq.model?.toLowerCase().includes(term) ||
                                    unit.status?.toLowerCase().includes(term);
                                })
                                .map((unit) => {
                                  const statusInfo = getStatusInfo(unit.status);
                                  const StatusIcon = statusInfo.icon;
                                  return (
                                    <div 
                                      key={unit.id} 
                                      className="flex items-center justify-between p-3 bg-background rounded-lg border"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${statusInfo.bg}`}>
                                          <Hash className={`h-4 w-4 ${statusInfo.color}`} />
                                        </div>
                                        <div className="space-y-1">
                                          <div className="flex items-center gap-2">
                                            <p className="font-medium">{eq.name}</p>
                                            <span className="text-muted-foreground">|</span>
                                            <p className="font-mono text-sm text-primary">
                                              <span className="text-muted-foreground font-normal">N° Serie:</span> {unit.serial_number}
                                            </p>
                                            {(unit.serial_number === 'NA' || unit.serial_number === 'N/A') && (
                                              <span className="text-amber-500" title="Sin número de serie">
                                                <AlertTriangle className="h-3.5 w-3.5" />
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <Badge variant="outline" className="text-xs">
                                              <MapPin className="h-3 w-3 mr-1" />
                                              {locGroup.location_name}
                                            </Badge>
                                            <Badge variant="outline" className="text-xs">
                                              {eq.category_name}
                                            </Badge>
                                            <Badge className={`${statusInfo.bg} ${statusInfo.color} border-0 text-xs`}>
                                              <StatusIcon className="h-3 w-3 mr-1" />
                                              {statusInfo.label}
                                            </Badge>
                                            {unit.receptor_number && (
                                              <Badge variant="secondary" className="text-xs">
                                                {unit.receptor_number}
                                              </Badge>
                                            )}
                                            {unit.notes && (
                                              <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                                                📝 {unit.notes}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex gap-1">
                                        {unit.status !== 'disponible' && (
                                          <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            onClick={() => openUnitDetail(unit.id)}
                                            title="Ver detalles del incidente"
                                            data-testid={`unit-detail-${unit.id}`}
                                          >
                                            <Eye className="h-4 w-4 text-amber-500" />
                                          </Button>
                                        )}
                                        <Button 
                                          variant="ghost" 
                                          size="icon" 
                                          onClick={() => openUnitModal(eq, unit)}
                                          title="Editar unidad"
                                        >
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button 
                                          variant="ghost" 
                                          size="icon" 
                                          className="text-destructive"
                                          onClick={() => handleDeleteUnit(unit.id, eq.id)}
                                          title="Eliminar unidad"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-muted/30 rounded-lg">
                        <Hash className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                        <p className="text-sm text-muted-foreground mb-3">
                          No hay unidades individuales registradas
                        </p>
                        <div className="flex justify-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => openUnitModal(eq)}>
                            <Plus className="h-4 w-4 mr-1" /> Agregar Unidad
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openBulkUnitModal(eq)}>
                            <Hash className="h-4 w-4 mr-1" /> Agregar Múltiples
                          </Button>
                        </div>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>
      </>
      )}

      {/* Equipment Modal */}
      <EquipmentModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        equipment={editingEquipment}
        categories={categories}
        api={api}
        onSuccess={handleEquipmentSuccess}
      />

      {/* Individual Unit Modal */}
      <UnitModal
        open={isUnitModalOpen}
        onOpenChange={setIsUnitModalOpen}
        unit={editingUnit}
        equipment={selectedEquipmentForUnits}
        locations={locations}
        categories={categories}
        api={api}
        onSuccess={handleUnitSuccess}
      />

      {/* Bulk Units Modal */}
      <BulkUnitModal
        open={isBulkUnitModalOpen}
        onOpenChange={setIsBulkUnitModalOpen}
        equipment={selectedEquipmentForUnits}
        locations={locations}
        api={api}
        onSuccess={handleUnitSuccess}
      />

      {/* Condition History Modal */}
      <ConditionHistoryModal
        open={isHistoryModalOpen}
        onOpenChange={setIsHistoryModalOpen}
        equipmentId={selectedEquipmentForHistory?.id}
        equipmentName={selectedEquipmentForHistory?.name}
      />

      {/* Barcode Scanner for Inventory Search */}
      <BarcodeScanner
        isOpen={showInventoryScanner}
        onClose={() => setShowInventoryScanner(false)}
        onScanSuccess={(result) => {
          if (result.type === 'unit' && result.entity?.serial_number) {
            setSearchTerm(result.entity.serial_number);
          } else if (result.type === 'equipment' && result.entity?.name) {
            setSearchTerm(result.entity.name);
          }
          setShowInventoryScanner(false);
        }}
        scanMode="any"
        showHistory={false}
      />

      {/* Unit Detail Modal */}
      <Dialog open={showUnitDetail} onOpenChange={setShowUnitDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-500" />
              Detalles de Unidad
            </DialogTitle>
          </DialogHeader>
          {unitDetailLoading ? (
            <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>
          ) : unitDetailData ? (
            <div className="space-y-4" data-testid="unit-detail-modal">
              {/* Unit Info */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-lg">{unitDetailData.equipment_name}</p>
                    <p className="text-sm text-muted-foreground font-mono">S/N: {unitDetailData.unit?.serial_number}</p>
                    {unitDetailData.unit?.receptor_number && (
                      <p className="text-sm text-muted-foreground">{unitDetailData.unit.receptor_number}</p>
                    )}
                  </div>
                  <Badge className={`${getStatusInfo(unitDetailData.unit?.status).bg} ${getStatusInfo(unitDetailData.unit?.status).color} border-0`}>
                    {getStatusInfo(unitDetailData.unit?.status).label}
                  </Badge>
                </div>
                {unitDetailData.unit?.fault_reason && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-sm font-medium text-red-400">Razon de falla:</p>
                    <p className="text-sm">{unitDetailData.unit.fault_reason}</p>
                  </div>
                )}
                {(unitDetailData.unit?.status === 'falla' || unitDetailData.unit?.status === 'con_falla') && (
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={handleResolveFault}
                    disabled={resolvingFault}
                    data-testid="resolve-fault-btn"
                  >
                    {resolvingFault ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Resolver Falla
                  </Button>
                )}
              </div>

              {/* Condition Changes */}
              {unitDetailData.condition_changes?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <History className="h-4 w-4 text-primary" /> Cambios de Estado
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {unitDetailData.condition_changes.map((c, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-muted/20 rounded text-sm">
                        <div className="flex-shrink-0 w-20 text-xs text-muted-foreground">{c.date?.slice(0,10)}</div>
                        <Badge variant="outline" className="text-xs">{c.previous_status}</Badge>
                        <span className="text-muted-foreground">→</span>
                        <Badge className={`${getStatusInfo(c.new_status).bg} ${getStatusInfo(c.new_status).color} border-0 text-xs`}>
                          {c.new_status}
                        </Badge>
                        {c.reason && <span className="text-xs text-muted-foreground truncate">{c.reason}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Movement History */}
              {unitDetailData.movement_history?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Package className="h-4 w-4 text-blue-500" /> Historial de Movimientos
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {unitDetailData.movement_history.map((m, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/20 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {m.type === 'salida' ? 'Salida' : m.type === 'retorno' ? 'Retorno' : 'Prestamo'}
                          </Badge>
                          <span>{m.event_name || m.client_name || '-'}</span>
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          <p>{m.date?.slice(0,10)}</p>
                          <p>{m.responsible_name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Maintenance Records */}
              {unitDetailData.maintenance_records?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-amber-500" /> Mantenimientos
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {unitDetailData.maintenance_records.map((m, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/20 rounded text-sm">
                        <div>
                          <Badge variant="outline" className="text-xs mr-2">{m.type}</Badge>
                          <span className="text-xs">{m.description?.slice(0, 60)}</span>
                        </div>
                        <div className="text-right">
                          <Badge className={m.status === 'completado' ? 'bg-emerald-500/20 text-emerald-400' : m.status === 'pendiente' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'} >
                            {m.status}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">{m.scheduled_date?.slice(0,10)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!unitDetailData.condition_changes?.length && !unitDetailData.movement_history?.length && !unitDetailData.maintenance_records?.length && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No hay historial registrado para esta unidad</p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      {/* Import Report Modal */}
      <Dialog open={!!importReport} onOpenChange={() => setImportReport(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="import-report-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Reporte de Importación
            </DialogTitle>
          </DialogHeader>
          
          {importReport && (
            <div className="space-y-4">
              {/* File info */}
              <p className="text-sm text-muted-foreground">
                Archivo: <span className="font-medium">{importReport.filename}</span>
                {importReport.total_rows > 0 && <> — {importReport.total_rows} filas procesadas</>}
              </p>

              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3 text-center bg-emerald-500/10 border-emerald-500/30">
                  <p className="text-2xl font-bold text-emerald-400">{importReport.imported || 0}</p>
                  <p className="text-xs text-muted-foreground">Nuevas</p>
                </div>
                <div className="rounded-lg border p-3 text-center bg-blue-500/10 border-blue-500/30">
                  <p className="text-2xl font-bold text-blue-400">{importReport.updated || 0}</p>
                  <p className="text-xs text-muted-foreground">Actualizadas</p>
                </div>
                <div className={`rounded-lg border p-3 text-center ${importReport.total_errors > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-muted/30 border-border'}`}>
                  <p className={`text-2xl font-bold ${importReport.total_errors > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>{importReport.total_errors || 0}</p>
                  <p className="text-xs text-muted-foreground">Errores</p>
                </div>
              </div>

              {/* Columns detected */}
              {importReport.columns_found?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">Columnas detectadas:</span>{' '}
                  {importReport.columns_found.join(', ')}
                </div>
              )}

              {/* Errors section */}
              {importReport.errors?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-red-400 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" /> Errores ({importReport.total_errors})
                  </h4>
                  <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 space-y-1 max-h-40 overflow-y-auto">
                    {importReport.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-300">{err}</p>
                    ))}
                  </div>
                  <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
                    <p className="text-xs text-yellow-300 font-medium">Cómo corregir:</p>
                    <ul className="text-xs text-yellow-300/80 list-disc list-inside mt-1 space-y-0.5">
                      <li>Verifica que la columna <code className="bg-yellow-500/20 px-1 rounded">Nombre_Equipo</code> tenga valores en cada fila</li>
                      <li>Estados válidos: disponible, asignado, en_prestamo, mantenimiento_interno, mantenimiento_externo, falla, perdido_recuperar, perdido_definitivo, baja_perdida, baja_definitivo</li>
                      <li>Si usas la plantilla exportada, no modifiques los nombres de columna</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Details table */}
              {importReport.details?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Detalle por fila</h4>
                  <div className="rounded-lg border max-h-60 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Fila</th>
                          <th className="px-2 py-1.5 text-left">Resultado</th>
                          <th className="px-2 py-1.5 text-left">Serie</th>
                          <th className="px-2 py-1.5 text-left">Equipo</th>
                          <th className="px-2 py-1.5 text-left">Estado</th>
                          <th className="px-2 py-1.5 text-left">No. de Equipo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {importReport.details.map((d, i) => (
                          <tr key={i} className={d.status === 'error' ? 'bg-red-500/5' : ''}>
                            <td className="px-2 py-1">{d.row}</td>
                            <td className="px-2 py-1">
                              <Badge variant={d.status === 'imported' ? 'default' : d.status === 'updated' ? 'secondary' : 'destructive'} className="text-[10px]">
                                {d.status === 'imported' ? 'Nueva' : d.status === 'updated' ? 'Actualizada' : 'Error'}
                              </Badge>
                            </td>
                            <td className="px-2 py-1 font-mono">{d.serial || '-'}</td>
                            <td className="px-2 py-1 max-w-[150px] truncate">{d.equip || d.reason || '-'}</td>
                            <td className="px-2 py-1">{d.estado || '-'}</td>
                            <td className="px-2 py-1">{d.receptor || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Success message when all good */}
              {importReport.imported === 0 && importReport.updated === 0 && importReport.total_errors === 0 && (
                <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4">
                  <p className="text-sm text-yellow-300 font-medium">No se procesó ninguna fila</p>
                  <ul className="text-xs text-yellow-300/80 list-disc list-inside mt-2 space-y-1">
                    <li>Revisa que el archivo tenga datos debajo de la fila de encabezados</li>
                    <li>Columna requerida: <code className="bg-yellow-500/20 px-1 rounded">Nombre_Equipo</code></li>
                    <li>Descarga la plantilla actualizada para referencia</li>
                  </ul>
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={() => setImportReport(null)} data-testid="close-import-report">
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default InventoryPage;
